import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { AUTH_RETURN_TO_KEY, DEFAULT_POST_LOGIN_SLUG } from '../../constants/adminSettings';
import './AuthCallback.css';

const AuthCallback: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const { data, error } = await supabaseClient.auth.getSession();
        
        if (error) {
          console.error('Auth callback error:', error);
          setError(error.message);
        } else if (data.session) {
          // Use saved return path (avoid sending user back to landing page)
          let target = '';
          try {
            target = sessionStorage.getItem(AUTH_RETURN_TO_KEY) || '';
            sessionStorage.removeItem(AUTH_RETURN_TO_KEY);
          } catch (_e) { /* ignore */ }
          const isValidReturn = target && target !== '/' && !target.startsWith('/auth') && !target.startsWith('/login');
          if (isValidReturn) {
            navigate(target, { replace: true });
          } else {
            // Fallback: first location's overview (not landing)
            await supabaseLocationService.initialize();
            const locs = supabaseLocationService.getAvailableLocations();
            const first = locs[0];
            const slug = first ? supabaseLocationService.getSlugForLocation(first.name) : null;
            const fallback = slug ? `/${slug}` : `/${DEFAULT_POST_LOGIN_SLUG}`;
            navigate(fallback, { replace: true });
          }
        } else {
          // No session: still avoid landing if we have a return path
          let target = '';
          try {
            target = sessionStorage.getItem(AUTH_RETURN_TO_KEY) || '';
            sessionStorage.removeItem(AUTH_RETURN_TO_KEY);
          } catch (_e) { /* ignore */ }
          const isValidReturn = target && target !== '/' && !target.startsWith('/auth') && !target.startsWith('/login');
          navigate(isValidReturn ? target : `/${DEFAULT_POST_LOGIN_SLUG}`, { replace: true });
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    handleAuthCallback();
  }, [navigate]);

  if (loading) {
    return (
      <div className="auth-callback">
        <div className="auth-callback-content">
          <div className="auth-callback-spinner"></div>
          <h2>Completing sign in...</h2>
          <p>Please wait while we complete your authentication.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-callback">
        <div className="auth-callback-content">
          <div className="auth-callback-error">
            <h2>Authentication Error</h2>
            <p>{error}</p>
            <button 
              className="auth-callback-button"
              onClick={() => navigate('/')}
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-callback">
      <div className="auth-callback-content">
        <div className="auth-callback-success">
          <h2>Successfully signed in!</h2>
          <p>Redirecting you back...</p>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
